import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  HeartHandshake,
  ExternalLink,
  Heart,
  MapPin,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Facebook,
  Instagram,
  Upload,
  Sparkles,
  Award,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SEO from "../components/SEO";

const ANIMAL_OPTIONS = [
  { value: "dogs",  label: "Dogs" },
  { value: "cats",  label: "Cats" },
  { value: "both",  label: "Both dogs & cats" },
  { value: "other", label: "Other animals" },
];

function animalLabel(v) {
  const m = ANIMAL_OPTIONS.find((o) => o.value === v);
  return m ? m.label : v;
}

function RescueCard({ r }) {
  const socials = [
    { url: r.social_facebook,  Icon: Facebook,  label: "Facebook" },
    { url: r.social_instagram, Icon: Instagram, label: "Instagram" },
  ].filter((s) => s.url);
  const isFounding = !!r.is_founding_partner;
  return (
    <div
      className={`relative border bg-white p-5 flex flex-col hover-shadow-harsh ${
        isFounding ? "border-yellow-700 shadow-[0_0_0_2px_rgba(202,138,4,0.15)]" : "border-zinc-900"
      }`}
      data-testid={`rescue-card-${r.id}`}
    >
      {isFounding && (
        <div
          className="absolute -top-3 left-4 inline-flex items-center gap-1 bg-yellow-400 text-yellow-950 border border-yellow-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
          data-testid={`rescue-founding-ribbon-${r.id}`}
        >
          <Award size={11}/> Founding Rescue Partner
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-20 h-20 border border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
          {r.logo_url ? (
            <img
              src={r.logo_url}
              alt={`${r.name} logo`}
              className="w-full h-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <HeartHandshake size={32} className="text-zinc-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow">
            <MapPin size={10} className="inline mr-1 -mt-0.5" />
            {r.city}, {r.state_code || r.state}
          </div>
          <h3 className="font-display font-black text-lg leading-tight mt-1" data-testid={`rescue-name-${r.id}`}>
            {r.name}
          </h3>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-zinc-300 bg-zinc-50 text-zinc-700">
              {animalLabel(r.animals)}
              {r.animals === "other" && r.animals_other ? ` · ${r.animals_other}` : ""}
            </span>
            {isFounding && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-yellow-700 bg-yellow-50 text-yellow-900"
                data-testid={`rescue-founding-badge-${r.id}`}
              >
                <Award size={10}/> Founding
              </span>
            )}
            {r.is_featured && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-red-700 bg-red-50 text-red-700">
                <Sparkles size={10} /> Featured
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="text-sm text-zinc-700 leading-relaxed mt-4 flex-1">
        {r.description}
      </p>

      {socials.length > 0 && (
        <div className="mt-4 flex items-center gap-3">
          {socials.map(({ url, Icon, label }) => (
            <a
              key={label}
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label={`${r.name} on ${label}`}
              className="text-zinc-500 hover:text-red-600 transition-colors"
              data-testid={`rescue-${label.toLowerCase()}-${r.id}`}
            >
              <Icon size={16} />
            </a>
          ))}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2 pt-4 border-t border-zinc-200">
        {r.website && (
          <a
            href={r.website}
            target="_blank"
            rel="noreferrer"
            className="btn-outline text-xs"
            data-testid={`rescue-visit-${r.id}`}
          >
            <ExternalLink size={12} /> Visit Rescue
          </a>
        )}
        {r.donate_url && (
          <a
            href={r.donate_url}
            target="_blank"
            rel="noreferrer"
            className="btn-danger text-xs"
            data-testid={`rescue-donate-${r.id}`}
          >
            <Heart size={12} /> Donate
          </a>
        )}
      </div>
    </div>
  );
}

function ApplyModal({ open, onClose }) {
  const [f, setF] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    city: "",
    state: "",
    state_code: "",
    website: "",
    social_facebook: "",
    social_instagram: "",
    social_tiktok: "",
    social_x: "",
    animals: "both",
    animals_other: "",
    description: "",
    donate_url: "",
    logo_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLogoUploading(true);
    try {
      const { data } = await api.post("/rescue-partners/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((s) => ({ ...s, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed. You can also paste a public URL below.");
    } finally {
      setLogoUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.contact_name || !f.contact_email || !f.city || !f.state || !f.description) {
      toast.error("Please fill in the required fields (marked with *).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/rescue-partners/apply", f);
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-zinc-900/80 backdrop-blur-sm overflow-y-auto p-4"
      onClick={onClose}
      data-testid="rescue-apply-modal-backdrop"
    >
      <div
        className="w-full max-w-2xl bg-white border border-zinc-900 shadow-2xl my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="rescue-apply-modal"
      >
        <div className="flex items-center justify-between bg-zinc-900 text-white px-5 py-3 sticky top-0">
          <div className="flex items-center gap-2">
            <HeartHandshake size={18} className="text-red-500" />
            <div className="font-display font-black text-sm uppercase tracking-widest">
              Become a Rescue Partner
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:text-red-400" data-testid="rescue-apply-close">
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center" data-testid="rescue-apply-thanks">
            <CheckCircle2 size={40} className="text-red-600 mx-auto" />
            <div className="font-display font-black text-2xl mt-4">Application received</div>
            <p className="text-sm text-zinc-700 mt-3 max-w-md mx-auto leading-relaxed">
              Thanks — your rescue is now in our review queue. We'll email
              you at <b>{f.contact_email}</b> as soon as your listing is
              approved and live on Paws on Record.
            </p>
            <button onClick={onClose} className="btn-danger mt-6 text-xs" data-testid="rescue-apply-done">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-xs text-zinc-500 leading-relaxed">
              Free to join. Submissions go to the site owner for review before
              anything is published. All fields marked <span className="text-red-600 font-bold">*</span> are required.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Rescue name *"><input value={f.name} onChange={upd("name")} className="input" data-testid="rescue-apply-name" /></Field>
              <Field label="Website"><input value={f.website} onChange={upd("website")} placeholder="https://..." className="input" data-testid="rescue-apply-website" /></Field>
              <Field label="Contact name *"><input value={f.contact_name} onChange={upd("contact_name")} className="input" data-testid="rescue-apply-contact-name" /></Field>
              <Field label="Contact email *"><input type="email" value={f.contact_email} onChange={upd("contact_email")} className="input" data-testid="rescue-apply-contact-email" /></Field>
              <Field label="City *"><input value={f.city} onChange={upd("city")} className="input" data-testid="rescue-apply-city" /></Field>
              <Field label="State *"><input value={f.state} onChange={upd("state")} placeholder="e.g. California" className="input" data-testid="rescue-apply-state" /></Field>
            </div>

            <Field label="Animals rescued *">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ANIMAL_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setF((s) => ({ ...s, animals: o.value }))}
                    data-testid={`rescue-apply-animals-${o.value}`}
                    className={`py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
                      f.animals === o.value
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {f.animals === "other" && (
                <input
                  value={f.animals_other}
                  onChange={upd("animals_other")}
                  placeholder="Specify (e.g. horses, birds, exotics)"
                  className="input mt-2"
                  data-testid="rescue-apply-animals-other"
                />
              )}
            </Field>

            <Field label="Short description *">
              <textarea
                value={f.description}
                onChange={upd("description")}
                rows={3}
                placeholder="One or two sentences on your mission, service area, and what makes you distinct."
                className="input resize-none"
                data-testid="rescue-apply-description"
              />
            </Field>

            <Field label="Logo (optional)">
              <div className="flex items-center gap-3">
                <label className="btn-outline text-xs cursor-pointer" data-testid="rescue-apply-logo-picker">
                  <Upload size={12} />
                  {logoUploading ? "Uploading…" : "Upload PNG/JPG"}
                  <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" disabled={logoUploading} />
                </label>
                {f.logo_url && (
                  <div className="w-12 h-12 border border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
                    <img src={f.logo_url} alt="Logo preview" className="w-full h-full object-contain" />
                  </div>
                )}
                <span className="text-[11px] text-zinc-500 leading-tight">or paste a public URL below</span>
              </div>
              <input
                value={f.logo_url}
                onChange={upd("logo_url")}
                placeholder="https://.../logo.png"
                className="input mt-2 text-xs"
                data-testid="rescue-apply-logo-url"
              />
            </Field>

            <details className="border border-zinc-200 px-3 py-2">
              <summary className="text-xs font-bold uppercase tracking-widest cursor-pointer">Social + donation links (optional)</summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <Field label="Facebook"><input value={f.social_facebook} onChange={upd("social_facebook")} className="input" placeholder="https://facebook.com/..." /></Field>
                <Field label="Instagram"><input value={f.social_instagram} onChange={upd("social_instagram")} className="input" placeholder="https://instagram.com/..." /></Field>
                <Field label="TikTok"><input value={f.social_tiktok} onChange={upd("social_tiktok")} className="input" placeholder="https://tiktok.com/@..." /></Field>
                <Field label="X (Twitter)"><input value={f.social_x} onChange={upd("social_x")} className="input" placeholder="https://x.com/..." /></Field>
                <Field label="Donate URL" className="sm:col-span-2"><input value={f.donate_url} onChange={upd("donate_url")} className="input" placeholder="https://.../donate" /></Field>
              </div>
            </details>

            <div className="pt-3 flex items-center justify-end gap-2 border-t border-zinc-200">
              <button type="button" onClick={onClose} className="btn-outline text-xs" data-testid="rescue-apply-cancel">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-danger text-xs disabled:opacity-60" data-testid="rescue-apply-submit">
                {submitting ? <><Loader2 size={12} className="animate-spin" /> Submitting</> : <><HeartHandshake size={12} /> Submit for Review</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="label-eyebrow mb-1.5">{label}</div>
      {children}
    </label>
  );
}

export default function RescuePartners() {
  const [rescues, setRescues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [q, setQ] = useState("");
  const [animalFilter, setAnimalFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/rescue-partners", {
          params: { q: q || undefined, animals: animalFilter || undefined, limit: 100 },
        });
        if (!cancelled) setRescues(data.items || []);
      } catch (err) {
        if (!cancelled) toast.error("Couldn't load rescue partners.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, animalFilter]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="rescue-partners-page">
      <SEO
        title="Rescue Partners · Paws on Record"
        description="Meet the animal rescue organizations partnered with Paws on Record — supporting the people who give animals a second chance."
        path="/rescue-partners"
      />

      <div className="label-eyebrow">Section · Rescue Partners</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
        Paws on Record Rescue Partners.
      </h1>
      <p className="font-display text-lg text-zinc-700 mt-3 max-w-2xl">
        Supporting the people who give animals a second chance.
      </p>
      <p className="text-base text-zinc-700 mt-5 max-w-3xl leading-relaxed">
        Paws on Record is proud to support animal rescue organizations working
        to protect, rehabilitate and rehome animals in need.
      </p>

      {/* Search & filters */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by rescue name or city…"
          className="input max-w-md flex-1 min-w-[220px]"
          data-testid="rescue-search"
        />
        <div className="flex items-center gap-1 border border-zinc-300 bg-white overflow-hidden">
          {["", ...ANIMAL_OPTIONS.map((o) => o.value)].map((v) => (
            <button
              key={v || "all"}
              onClick={() => setAnimalFilter(v)}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                animalFilter === v ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`rescue-filter-${v || "all"}`}
            >
              {v === "" ? "All" : v}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setApplyOpen(true)}
          className="btn-danger ml-auto text-xs"
          data-testid="rescue-apply-open-top"
        >
          <HeartHandshake size={12} /> Become a Founding Partner
        </button>
      </div>

      {/* Results grid */}
      <div className="mt-8">
        {loading ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2" data-testid="rescue-loading">
            <Loader2 size={14} className="animate-spin" /> Loading rescue partners…
          </div>
        ) : rescues.length === 0 ? (
          <div className="border border-zinc-900 bg-white p-10 sm:p-14 text-center" data-testid="rescue-empty">
            <HeartHandshake size={40} className="mx-auto text-red-600" />
            <div className="label-eyebrow mt-4 text-red-600">Founding rescue partners wanted</div>
            <div className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-2">
              Be one of the first partners.
            </div>
            <p className="text-sm text-zinc-700 mt-4 max-w-xl mx-auto leading-relaxed">
              Paws on Record is currently accepting applications from animal
              rescue organizations for our founding partner group. Every
              approved partner will be featured on the homepage, listed here
              with your logo and links, and highlighted in future outreach.
              <span className="ml-2 inline-block px-2 py-0.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest align-middle">Free to join</span>
            </p>
            <button
              type="button"
              onClick={() => setApplyOpen(true)}
              className="btn-danger mt-6 text-xs"
              data-testid="rescue-empty-apply"
            >
              <HeartHandshake size={12} /> Become a Founding Rescue Partner
            </button>
            <div className="mt-8 text-[12px] text-zinc-500 max-w-xl mx-auto leading-relaxed">
              Looking for local shelters, animal-control agencies, or
              national orgs like the ASPCA / HSUS / Best Friends?{" "}
              <Link to="/local-shelters" className="underline font-bold">
                See Local Shelters &amp; Animal Resources →
              </Link>
              <br />
              Those organizations are not Paws on Record partners; they are
              listed as a public resource.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rescue-grid">
            {rescues.map((r) => (<RescueCard key={r.id} r={r} />))}
          </div>
        )}
      </div>

      {/* Become-a-partner CTA */}
      <div className="mt-16 border border-zinc-900 bg-zinc-900 text-white p-8 sm:p-10" data-testid="rescue-cta">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="label-eyebrow text-red-500">Are you an animal rescue?</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
              Become a Founding Rescue Partner.
            </h2>
            <p className="text-sm text-zinc-300 mt-3 max-w-xl leading-relaxed">
              Join the Paws on Record Rescue Partner network and help us connect more people with organizations protecting animals every day.
              <span className="ml-2 inline-block px-2 py-0.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest align-middle">Free to join</span>
            </p>
          </div>
          <div className="md:text-right">
            <button
              type="button"
              onClick={() => setApplyOpen(true)}
              className="w-full md:w-auto bg-red-600 hover:bg-red-500 text-white px-6 py-4 font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2"
              data-testid="rescue-apply-open-bottom"
            >
              <HeartHandshake size={16} /> Become a Founding Rescue Partner
            </button>
          </div>
        </div>
      </div>

      {/* Disclaimer — strong non-endorsement clarity */}
      <div className="mt-10 text-[13px] text-zinc-600 leading-relaxed max-w-3xl border-t-2 border-zinc-900 pt-6" data-testid="rescue-disclaimer">
        <span className="inline-block label-eyebrow mb-2">Editorial policy</span>
        <br />
        Only organizations that have applied and been approved appear on this
        page. A Paws on Record Rescue Partner listing means the rescue has
        joined our partner network — it is not a general endorsement of every
        specific action of the organization. Shelters, animal-control
        agencies, and other resources appear on our{" "}
        <Link to="/local-shelters" className="underline font-bold">
          Local Shelters &amp; Animal Resources
        </Link>{" "}
        page and are NOT partners unless specifically marked as such.
      </div>

      <ApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} />
    </div>
  );
}
